import { Resolution } from '@blockscout/stats-types';
import type { StatsIntervalIds } from 'types/client/stats';

type TranslateFn = (key: string) => string;

export const STATS_KEYS = {
  title: {
    'default': 'stats.title.default',
    enhancedData: 'stats.title.enhancedData',
  },
} as const;

export const STATS_FILTER_KEYS = {
  allStats: 'stats.filters.allStats',
  placeholder: 'stats.placeholder.search',
} as const;

export const STATS_INTERVAL_KEYS: Record<StatsIntervalIds, { title: string; shortTitle: string }> = {
  all: {
    title: 'stats.intervals.all.title',
    shortTitle: 'stats.intervals.all.shortTitle',
  },
  oneMonth: {
    title: 'stats.intervals.oneMonth.title',
    shortTitle: 'stats.intervals.oneMonth.shortTitle',
  },
  threeMonths: {
    title: 'stats.intervals.threeMonths.title',
    shortTitle: 'stats.intervals.threeMonths.shortTitle',
  },
  sixMonths: {
    title: 'stats.intervals.sixMonths.title',
    shortTitle: 'stats.intervals.sixMonths.shortTitle',
  },
  oneYear: {
    title: 'stats.intervals.oneYear.title',
    shortTitle: 'stats.intervals.oneYear.shortTitle',
  },
};

export const STATS_RESOLUTION_KEYS: Record<Resolution, string> = {
  [Resolution.RESOLUTION_UNSPECIFIED]: 'stats.resolutions.day',
  [Resolution.DAY]: 'stats.resolutions.day',
  [Resolution.WEEK]: 'stats.resolutions.week',
  [Resolution.MONTH]: 'stats.resolutions.month',
  [Resolution.YEAR]: 'stats.resolutions.year',
  [Resolution.UNRECOGNIZED]: 'stats.resolutions.day',
};

export const STATS_CHART_KEYS = {
  actions: {
    clickAgain: 'stats.actions.clickAgain',
    copyLink: 'stats.actions.copyLink',
    openChartOptionsMenu: 'stats.actions.openChartOptionsMenu',
    reloadPage: 'stats.actions.reloadPage',
    resetZoom: 'stats.actions.resetZoom',
    saveAsCsv: 'stats.actions.saveAsCsv',
    saveAsPng: 'stats.actions.saveAsPng',
    share: 'stats.actions.share',
    viewFullscreen: 'stats.actions.viewFullscreen',
  },
  download: {
    imageSuffix: 'stats.download.imageSuffix',
    csvSuffix: 'stats.download.csvSuffix',
  },
  empty: {
    noData: 'stats.empty.noData',
    noMatches: 'stats.empty.noMatches',
  },
  errors: {
    chartDataLoadFailed: 'stats.errors.chartDataLoadFailed',
    chartsLoadFailed: 'stats.errors.chartsLoadFailed',
  },
  fields: {
    date: 'stats.fields.date',
    dates: 'stats.fields.dates',
    month: 'stats.fields.month',
    value: 'stats.fields.value',
    year: 'stats.fields.year',
  },
  tooltip: {
    incompleteResolution: 'stats.tooltip.incompleteResolution',
  },
} as const;

const STATS_SECTION_TITLE_KEYS: Record<string, string> = {
  accounts: 'stats.api.sections.accounts',
  transactions: 'stats.api.sections.transactions',
  blocks: 'stats.api.sections.blocks',
  tokens: 'stats.api.sections.tokens',
  gas: 'stats.api.sections.gas',
  contracts: 'stats.api.sections.contracts',
  user_ops: 'stats.api.sections.userOps',
};

const STATS_CHART_TITLE_KEYS: Record<string, string> = {
  accountsGrowth: 'stats.api.charts.accountsGrowth.title',
  activeAccounts: 'stats.api.charts.activeAccounts.title',
  newAccounts: 'stats.api.charts.newAccounts.title',
  averageTxnFee: 'stats.api.charts.averageTxnFee.title',
  newTxns: 'stats.api.charts.newTxns.title',
  txnsFee: 'stats.api.charts.txnsFee.title',
  txnsGrowth: 'stats.api.charts.txnsGrowth.title',
  txnsSuccessRate: 'stats.api.charts.txnsSuccessRate.title',
  averageBlockRewards: 'stats.api.charts.averageBlockRewards.title',
  averageBlockSize: 'stats.api.charts.averageBlockSize.title',
  newBlocks: 'stats.api.charts.newBlocks.title',
  newNativeCoinTransfers: 'stats.api.charts.newNativeCoinTransfers.title',
  averageGasLimit: 'stats.api.charts.averageGasLimit.title',
  averageGasPrice: 'stats.api.charts.averageGasPrice.title',
  gasUsedGrowth: 'stats.api.charts.gasUsedGrowth.title',
  networkUtilization: 'stats.api.charts.networkUtilization.title',
  averageGasUsed: 'stats.api.charts.averageGasUsed.title',
  contractsGrowth: 'stats.api.charts.contractsGrowth.title',
  newContracts: 'stats.api.charts.newContracts.title',
  newVerifiedContracts: 'stats.api.charts.newVerifiedContracts.title',
  verifiedContractsGrowth: 'stats.api.charts.verifiedContractsGrowth.title',
};

const STATS_CHART_DESCRIPTION_KEYS: Record<string, string> = {
  accountsGrowth: 'stats.api.charts.accountsGrowth.description',
  activeAccounts: 'stats.api.charts.activeAccounts.description',
  newAccounts: 'stats.api.charts.newAccounts.description',
  averageTxnFee: 'stats.api.charts.averageTxnFee.description',
  newTxns: 'stats.api.charts.newTxns.description',
  txnsFee: 'stats.api.charts.txnsFee.description',
  txnsGrowth: 'stats.api.charts.txnsGrowth.description',
  txnsSuccessRate: 'stats.api.charts.txnsSuccessRate.description',
  averageBlockRewards: 'stats.api.charts.averageBlockRewards.description',
  averageBlockSize: 'stats.api.charts.averageBlockSize.description',
  newBlocks: 'stats.api.charts.newBlocks.description',
  newNativeCoinTransfers: 'stats.api.charts.newNativeCoinTransfers.description',
  averageGasLimit: 'stats.api.charts.averageGasLimit.description',
  averageGasPrice: 'stats.api.charts.averageGasPrice.description',
  gasUsedGrowth: 'stats.api.charts.gasUsedGrowth.description',
  networkUtilization: 'stats.api.charts.networkUtilization.description',
  averageGasUsed: 'stats.api.charts.averageGasUsed.description',
  contractsGrowth: 'stats.api.charts.contractsGrowth.description',
  newContracts: 'stats.api.charts.newContracts.description',
  newVerifiedContracts: 'stats.api.charts.newVerifiedContracts.description',
  verifiedContractsGrowth: 'stats.api.charts.verifiedContractsGrowth.description',
};

const STATS_COUNTER_TITLE_KEYS: Record<string, string> = {
  averageBlockTime: 'stats.api.counters.averageBlockTime.title',
  completedTxns: 'stats.api.counters.completedTxns.title',
  lastNewContracts: 'stats.api.counters.lastNewContracts.title',
  lastNewVerifiedContracts: 'stats.api.counters.lastNewVerifiedContracts.title',
  totalAccounts: 'stats.api.counters.totalAccounts.title',
  totalAddresses: 'stats.api.counters.totalAddresses.title',
  totalBlocks: 'stats.api.counters.totalBlocks.title',
  totalContracts: 'stats.api.counters.totalContracts.title',
  totalNativeCoinTransfers: 'stats.api.counters.totalNativeCoinTransfers.title',
  totalTokens: 'stats.api.counters.totalTokens.title',
  totalTxns: 'stats.api.counters.totalTxns.title',
  totalVerifiedContracts: 'stats.api.counters.totalVerifiedContracts.title',
  newTxns24h: 'stats.api.counters.newTxns24h.title',
  pendingTxns30m: 'stats.api.counters.pendingTxns30m.title',
  txnsFee24h: 'stats.api.counters.txnsFee24h.title',
  averageTxnFee24h: 'stats.api.counters.averageTxnFee24h.title',
};

const STATS_COUNTER_DESCRIPTION_KEYS: Record<string, string> = {
  averageBlockTime: 'stats.api.counters.averageBlockTime.description',
  completedTxns: 'stats.api.counters.completedTxns.description',
  lastNewContracts: 'stats.api.counters.lastNewContracts.description',
  lastNewVerifiedContracts: 'stats.api.counters.lastNewVerifiedContracts.description',
  totalAccounts: 'stats.api.counters.totalAccounts.description',
  totalAddresses: 'stats.api.counters.totalAddresses.description',
  totalBlocks: 'stats.api.counters.totalBlocks.description',
  totalContracts: 'stats.api.counters.totalContracts.description',
  totalNativeCoinTransfers: 'stats.api.counters.totalNativeCoinTransfers.description',
  totalTokens: 'stats.api.counters.totalTokens.description',
  totalTxns: 'stats.api.counters.totalTxns.description',
  totalVerifiedContracts: 'stats.api.counters.totalVerifiedContracts.description',
  newTxns24h: 'stats.api.counters.newTxns24h.description',
  pendingTxns30m: 'stats.api.counters.pendingTxns30m.description',
  txnsFee24h: 'stats.api.counters.txnsFee24h.description',
  averageTxnFee24h: 'stats.api.counters.averageTxnFee24h.description',
};

export const STATS_API_KEYS = {
  sections: STATS_SECTION_TITLE_KEYS,
  chartTitles: STATS_CHART_TITLE_KEYS,
  chartDescriptions: STATS_CHART_DESCRIPTION_KEYS,
  counterTitles: STATS_COUNTER_TITLE_KEYS,
  counterDescriptions: STATS_COUNTER_DESCRIPTION_KEYS,
} as const;

const translateKnownApiValue = (id: string, fallback: string | undefined, keys: Record<string, string>, t: TranslateFn) => {
  const key = keys[id];
  const translated = key ? t(key) : undefined;

  return translated && translated !== key ? translated : fallback;
};

export const getStatsSectionTitle = (id: string, fallback: string, t: TranslateFn) => {
  return translateKnownApiValue(id, fallback, STATS_SECTION_TITLE_KEYS, t) || fallback;
};

export const getStatsChartTitle = (id: string, fallback: string, t: TranslateFn) => {
  return translateKnownApiValue(id, fallback, STATS_CHART_TITLE_KEYS, t) || fallback;
};

export const getStatsChartDescription = (id: string, fallback: string | undefined, t: TranslateFn) => {
  return translateKnownApiValue(id, fallback, STATS_CHART_DESCRIPTION_KEYS, t);
};

export const getStatsCounterTitle = (id: string, fallback: string, t: TranslateFn) => {
  return translateKnownApiValue(id, fallback, STATS_COUNTER_TITLE_KEYS, t) || fallback;
};

export const getStatsCounterDescription = (id: string, fallback: string | undefined, t: TranslateFn) => {
  return translateKnownApiValue(id, fallback, STATS_COUNTER_DESCRIPTION_KEYS, t);
};
